import { Serwist } from '@serwist/window'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet'
import { ThemeProvider } from 'styled-components'
import useContent from 'store/content'
import * as themes from 'styles/themes'
import { getAugmentedReality } from 'store/actions/modules'
import { collectCardImageUrls, collectRAImages, checkIfCardPwa, extractPwaCards  } from 'utils/pwaUtils'

import './i18n'

import GDPRPopup from './GDPRPopup'
import PWABanner from './PWABanner'

import Header from './Header'
import Footer from './Footer'
import CardDisplay from './CardDisplay'
import WidescreenLogo from './Widescreen/Logo'
import WidescreenText from './Widescreen/Text'

import GlobalStyles from './globalStyles'
import { StyledLayout, StyledCard, StyledImageFooter, StyledImageContainer, WidescreenWrapper, WidescreenLeftPanel, WidescreenRightPanel, WidescreenRightPanelWrapper } from './styles'
import CacheDashboard from './PWABanner/CacheDashboard'

const MSJ_FOOTER_HEIGHT = 161

const Layout = () => {
  const [footerH, setFooterH] = useState(MSJ_FOOTER_HEIGHT)
  const dispatch = useDispatch()
  const content = useContent()
  const { appInfos, cardInfos, fbsInfos, themeInfos, visitorId, cardFolders, widescreenData, cardsData, isMobile, folders } = content.get.state.app
  const [customFavicon, setCustomFavicon] = useState('/static/favicon.ico')
  const [noInteraction, setNoInteraction] = useState(false);
  const themeName = (cardInfos.theme || appInfos.theme || appInfos.id).split('-').join('_')

  useEffect(() => {
    const el = document.getElementById('footer')
    if (el && el.clientHeight) {
      setFooterH(el.clientHeight + 1)
    }
  }, [])

  useEffect(() => {
    let selectedFavicon = customFavicon;

    if (cardInfos?.favicon) {
      selectedFavicon = cardInfos.favicon;
    } else if (cardFolders?.length > 0) {
      const folderWithFavicon = cardFolders?.find(folder => folder?.favicon);

      if (folderWithFavicon) {
        selectedFavicon = folderWithFavicon.favicon;
      }
    } else if (cardFolders?.favicon) {
      selectedFavicon = cardFolders.favicon;
    } else if (appInfos?.favicon) {
      selectedFavicon = appInfos.favicon;
    }

    setCustomFavicon(selectedFavicon);
  }, [appInfos, cardInfos, cardFolders]);

  useEffect(() => {
    let hasNoInteraction = false;

    if (cardInfos?.noInteraction === true) {
      hasNoInteraction = true;
    } else {
      const folderWithNoInteraction = cardFolders?.find(folder => folder?.noInteraction === true);

      if (folderWithNoInteraction) {
        hasNoInteraction = true;
      } else if (appInfos?.noInteraction === true) {
        hasNoInteraction = true;
      }
    }

    setNoInteraction(hasNoInteraction);
  }, [appInfos, cardInfos, cardFolders]);

  const noIndexDisable = !!appInfos?.seo?.enable && (
    !!cardInfos?.seo ||
    !!cardFolders?.some(folder => folder?.seo) ||
    !!appInfos.seo.activeAllApp
  )


  useEffect(() => {
    if (!noInteraction) return;

    const preventAction = (e) => e.preventDefault();

    document.addEventListener('copy', preventAction);
    document.addEventListener('cut', preventAction);
    document.addEventListener('paste', preventAction);

    return () => {
      document.removeEventListener('copy', preventAction);
      document.removeEventListener('cut', preventAction);
      document.removeEventListener('paste', preventAction);
    };
  }, [noInteraction]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return
    }

    const isEnabled = appInfos?.app_settings?.pwa_enabled
    const controller = navigator.serviceWorker.controller

    if (!isEnabled && controller) {
      controller.postMessage({ type: 'UNREGISTER' })
      return
    }

    if (!isEnabled || !checkIfCardPwa(cardInfos, folders)) {
      return
    }

    const run = async () => {
      const toNum = (v) => Number(v) || 0
      // Full page list for precaching (all token + card pages).
      const tokenPages = (appInfos.tokenList || []).map((token) => `/${appInfos.id}/${token}`)
      const cardPages = Object.keys(appInfos.cardList || {}).map((card) => `/${appInfos.id}/card/${card}`)
      const pages = [...tokenPages, ...cardPages]

      let serverVersions = null
      try {
        const res = await fetch(`/api/versions/${appInfos.id}`, { cache: 'no-store' })
        if (res.ok) serverVersions = await res.json()
      } catch (_) {}

      const versionKey = `pwa_page_versions_${appInfos.id}`
      let previousVersions = {}
      try {
        previousVersions = JSON.parse(localStorage.getItem(versionKey) || '{}')
      } catch (_) {}

      // Refetch only pages whose authoritative version is strictly newer than stored.
      const changedPages = serverVersions
        ? Object.keys(serverVersions).filter(
          (url) => url in previousVersions && toNum(serverVersions[url]) > toNum(previousVersions[url])
        )
        : []

      const cards = extractPwaCards(cardsData, folders);

      const images = collectCardImageUrls(cards, themeInfos, widescreenData)

      const arPartIds = []
      if (cards) {
        cards.forEach((card) => {
          card.parts.forEach((part) => {
            if ((part.type === 'AugmentedReality' || part.type === 'EnhancedReality') && part.id) {
              arPartIds.push(part.id)
            }
          })
        })
      }

      const raImageUrls = []
      for (const arId of arPartIds) {
        try {
          const ra = await dispatch(getAugmentedReality(appInfos.id, arId))
          raImageUrls.push(...collectRAImages(ra))
        } catch (_) { }
      }

      const wb = new Serwist('/sw.js', { scope: `/${appInfos.id}/` })

      await wb.register()

      wb.messageSW({ type: 'PRECACHE_URLS', urls: [...pages] })
      wb.messageSW({ type: 'IMAGE_URLS', urls: [...images, ...raImageUrls] })

      if (changedPages.length) {
        wb.messageSW({ type: 'REVALIDATE_URLS', urls: changedPages })
      }

      // Advance stored versions forward-only from the authoritative map. Skip
      // entirely if the fetch failed, so a transient error never rewrites the map.
      if (serverVersions) {
        const newVersions = { ...previousVersions }
        for (const url of Object.keys(serverVersions)) {
          const cur = serverVersions[url]
          if (cur != null && toNum(cur) > toNum(newVersions[url])) newVersions[url] = cur
        }
        if (JSON.stringify(newVersions) !== JSON.stringify(previousVersions)) {
          try {
            localStorage.setItem(versionKey, JSON.stringify(newVersions))
          } catch (_) {}
        }
      }
    }

    run().then(() => console.log("service worker registered"));
  }, [appInfos, themeName, dispatch, cardsData, themeInfos, widescreenData])

  const htmlProps = {
    id: appInfos ? appInfos.id : '',
    className: `token-${cardInfos ? cardInfos.tokenId : 'NONE'} card-${cardInfos ? cardInfos.cardId : 'NONE'
      } type-${cardInfos ? cardInfos.type : 'NONE'}`,
    'data-ua': appInfos?.tracking?.uaCode || '',
    'data-ga4': appInfos?.tracking?.ga4code || '',
  }

  const defaultTheme = themeInfos ? themes.msj : themes.msj_logo;
  const isWidescreenEnabled = appInfos?.widescreenEnable && !!widescreenData && !isMobile;

  return (
    <ThemeProvider theme={themes[themeName] || defaultTheme}>
      <GlobalStyles
        isListItem={htmlProps.className.includes('type-listitem')}
        $themeCustom={themeInfos}
        noInteraction={noInteraction}
      />
      <StyledLayout $isMobile={isMobile} $widescreen={isWidescreenEnabled}>
        <Helmet>
          {appInfos && <title>{appInfos.name}</title>}
          {appInfos?.app_settings?.pwa_enabled && checkIfCardPwa(cardInfos, folders) && (
            <link
              rel="manifest"
              href={`/api/manifest/${appInfos.id}?cardId=${encodeURIComponent(cardInfos.cardId)}`}
            />
          )}
          <link id="dynamic-favicon" rel="icon" type="image/png" href={customFavicon} />
          <link rel="icon" type="image/png" sizes="32x32" href={customFavicon} />
          <link rel="icon" type="image/png" sizes="16x16" href={customFavicon} />
          <link rel="apple-touch-icon" sizes="180x180" href={customFavicon} />
          <meta name="mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <html {...htmlProps} />
          {!noIndexDisable && <meta name="robots" content="noindex, nofollow" />}
        </Helmet>

        <span
          id="tag-ga-title"
          style={{
            visibility: 'hidden',
            height: 0,
          }}
        >
          {!cardInfos.tagGaChecked ? cardInfos.tagGaLabel : cardInfos.title}
        </span>

        {isWidescreenEnabled ? (
          <WidescreenWrapper
            $colorFirst={widescreenData.backgroundColorFirst}
            $colorSecond={widescreenData.backgroundColorSecond}
            $backgroundImage={widescreenData.backgroundImage}
          >
            <WidescreenLeftPanel
              $backgroundImage={widescreenData.backgroundImage}
              $colorFirst={widescreenData.backgroundColorFirst}
              $colorSecond={widescreenData.backgroundColorSecond}
            >
              {widescreenData.useLogo && <WidescreenLogo widescreenConfig={widescreenData} />}
              <WidescreenText widescreenConfig={widescreenData} />
            </WidescreenLeftPanel>
            <WidescreenRightPanelWrapper $borderFormat={widescreenData.borderFormat}>
              <WidescreenRightPanel $borderFormat={widescreenData.borderFormat}>
                <div
                  style={{
                    borderRadius:
                      widescreenData?.borderFormat === 'oris' || widescreenData?.borderFormat === 'osis'
                        ? '0px'
                        : '10px',
                    overflow: 'hidden',
                  }}
                >
                  <StyledCard id="main-card" $height={footerH} $themeCustom={themeInfos}>
                    {appInfos && cardInfos && (
                      <>
                        {appInfos.app_settings?.pwa_enabled && checkIfCardPwa(cardInfos, folders) && (
                          <PWABanner />
                        )}
                        <Header
                          appInfos={appInfos}
                          cardInfos={cardInfos}
                          themeInfos={themeInfos}
                          cardFolders={cardFolders}
                          isMobile={isMobile}
                          hideLogo={widescreenData?.hideLogo}
                        />
                      </>
                    )}
                    {appInfos && cardInfos && themeName && (
                      <CardDisplay
                        appInfos={appInfos}
                        cardInfos={cardInfos}
                        themeInfos={themeInfos}
                        visitorId={visitorId}
                        fbsInfos={fbsInfos}
                        isPwa={checkIfCardPwa(cardInfos, folders)}
                      />
                    )}
                  </StyledCard>

                  {themeName === 'mmq' && (
                    <StyledImageContainer>
                      <StyledImageFooter src="/static/background/mmq/footer.png" />
                    </StyledImageContainer>
                  )}

                  {themeName === 'megamigs' && (
                    <StyledImageContainer>
                      <StyledImageFooter src="/static/background/megamigs.svg" />
                    </StyledImageContainer>
                  )}

                  {themeName === 'larocheposay' && (
                    <StyledImageContainer style={{ margin: '0 0 0 9px' }}>
                      <StyledImageFooter
                        src="/static/background/larocheposay.png"
                        style={{ margin: '0 0 0 0' }}
                      />
                    </StyledImageContainer>
                  )}

                  {(themeName === 'expotempo2026fr' || themeName === 'expotempo2026en') && (
                    <StyledImageContainer style={{ margin: '0 0 0 9px', justifyItems: 'right' }}>
                      <StyledImageFooter
                        src="/static/background/bonsecours/bonsecours.png"
                        style={{ margin: '0 0 0 0' }}
                      />
                    </StyledImageContainer>
                  )}

                  {(themeName === 'expotempo2026zone2fr' || themeName === 'expotempo2026zone2en') && (
                    <StyledImageContainer style={{ margin: '0 0 0 9px', justifyItems: 'right' }}>
                      <StyledImageFooter
                        src="/static/background/bonsecours/bonsecours2.png"
                        style={{ margin: '0 0 0 0' }}
                      />
                    </StyledImageContainer>
                  )}

                  {(themeName === 'expotempo2026zone3fr' || themeName === 'expotempo2026zone3en') && (
                    <StyledImageContainer style={{ margin: '0 0 0 9px', justifyItems: 'right' }}>
                      <StyledImageFooter
                        src="/static/background/bonsecours/bonsecours3.png"
                        style={{ margin: '0 0 0 0' }}
                      />
                    </StyledImageContainer>
                  )}

                  <Footer appInfos={appInfos} themeName={themeName} themeInfos={themeInfos} />

                  <GDPRPopup />
                </div>
              </WidescreenRightPanel>
            </WidescreenRightPanelWrapper>
          </WidescreenWrapper>
        ) : (
          <>
            <StyledCard id="main-card" $height={footerH} $themeCustom={themeInfos}>
              {appInfos && cardInfos && (
                <>
                  {appInfos.app_settings?.pwa_enabled && checkIfCardPwa(cardInfos, folders) && <PWABanner />}
                  <Header
                    appInfos={appInfos}
                    cardInfos={cardInfos}
                    themeInfos={themeInfos}
                    cardFolders={cardFolders}
                    isMobile={isMobile}
                  />
                </>
              )}
              {appInfos && cardInfos && themeName && (
                <CardDisplay
                  appInfos={appInfos}
                  cardInfos={cardInfos}
                  themeInfos={themeInfos}
                  visitorId={visitorId}
                  fbsInfos={fbsInfos}
                  isPwa={checkIfCardPwa(cardInfos, folders)}
                />
              )}
            </StyledCard>

            {themeName === 'mmq' && (
              <StyledImageContainer>
                <StyledImageFooter src="/static/background/mmq/footer.png" />
              </StyledImageContainer>
            )}

            {themeName === 'megamigs' && (
              <StyledImageContainer>
                <StyledImageFooter src="/static/background/megamigs.svg" />
              </StyledImageContainer>
            )}

            {themeName === 'larocheposay' && (
              <StyledImageContainer style={{ margin: '0 0 0 9px' }}>
                <StyledImageFooter src="/static/background/larocheposay.png" style={{ margin: '0 0 0 0' }} />
              </StyledImageContainer>
            )}

            {(themeName === 'expotempo2026fr' || themeName === 'expotempo2026en') && (
              <StyledImageContainer style={{ margin: '0 0 0 9px', justifyItems: 'right' }}>
                <StyledImageFooter
                  src="/static/background/bonsecours/bonsecours.png"
                  style={{ margin: '0 0 0 0' }}
                />
              </StyledImageContainer>
            )}

            {(themeName === 'expotempo2026zone2fr' || themeName === 'expotempo2026zone2en') && (
              <StyledImageContainer style={{ margin: '0 0 0 9px', justifyItems: 'right' }}>
                <StyledImageFooter
                  src="/static/background/bonsecours/bonsecours2.png"
                  style={{ margin: '0 0 0 0' }}
                />
              </StyledImageContainer>
            )}

            {(themeName === 'expotempo2026zone3fr' || themeName === 'expotempo2026zone3en') && (
              <StyledImageContainer style={{ margin: '0 0 0 9px', justifyItems: 'right' }}>
                <StyledImageFooter
                  src="/static/background/bonsecours/bonsecours3.png"
                  style={{ margin: '0 0 0 0' }}
                />
              </StyledImageContainer>
            )}

            {themeName === 'ebj' && (
              <StyledImageContainer style={{ margin: '0 0 0 0', justifyItems: 'right' }}>
                <StyledImageFooter
                  src="/static/background/ebj/baspage.webp"
                  style={{ maxWidth: '100%', margin: '0 0 0 0' }}
                />
              </StyledImageContainer>
            )}

            {themeName === 'ebj_english' && (
              <StyledImageContainer style={{ margin: '0 0 0 0', justifyItems: 'right' }}>
                <StyledImageFooter
                  src="/static/background/ebj/baspage_en.webp"
                  style={{ maxWidth: '100%', margin: '0 0 0 0' }}
                />
              </StyledImageContainer>
            )}

            {themeName === 'villerosemere' && (
              <StyledImageContainer style={{ margin: '0 0 0 9px', justifyItems: 'right' }}>
                <StyledImageFooter
                  src="/static/background/villerosemere/logorosemere.png"
                  style={{ maxWidth: '100%', margin: '0 0 0 0' }}
                />
              </StyledImageContainer>
            )}

            {process.env.NODE_ENV === 'development' && <CacheDashboard />}

            <Footer appInfos={appInfos} themeName={themeName} themeInfos={themeInfos} />

            <GDPRPopup />
          </>
        )}
      </StyledLayout>
    </ThemeProvider>
  )
}

Layout.propTypes = {
  app: PropTypes.object,
}

export default Layout
